import { Helmet } from 'react-helmet-async';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui';

const faqData = [
  {
    question: 'How long does setup take?',
    answer:
      'Most teams are live in under 15 minutes. You upload your documents, the AI processes them in a few minutes, and then you copy one script tag onto your site. No developer required for the basic setup.',
  },
  {
    question: 'What file types can I upload?',
    answer:
      'ChytBot accepts PDF, DOCX, TXT, CSV, and plain-text Markdown files. You can also paste in URLs and ChytBot will crawl the page content. Maximum file size is 10 MB per upload.',
  },
  {
    question: "What's the difference between the Free and Pro plans?",
    answer:
      'The Free plan lets you explore the dashboard at no cost. To deploy a live chatbot you need the Starter plan ($9.99/mo), which includes 1 chatbot, up to 5 knowledge documents, and unlimited conversations. The Business plan ($19.99/mo) scales to 10 chatbots, 50 documents, and adds website extraction. Visit the pricing page for the full feature comparison.',
  },
  {
    question: 'Can I cancel anytime?',
    answer:
      'Yes. You can cancel your subscription at any time from your account settings. Your plan stays active until the end of the current billing period — no prorated charges, no cancellation fees.',
  },
  {
    question: 'How is my data kept private and secure?',
    answer:
      'Your uploaded documents are stored encrypted at rest and in transit. ChytBot never uses your content to train shared models — your data is isolated to your account. We are SOC 2 compliant and our full security policy is available on the Security page.',
  },
  {
    question: 'Which website builders does ChytBot work with?',
    answer:
      'ChytBot works with any site that lets you add a custom script tag — including Shopify, WordPress, Wix, Webflow, Squarespace, Framer, and custom-built sites. If you can add HTML, you can add ChytBot.',
  },
  {
    question: 'Can I try ChytBot for free?',
    answer:
      'Yes — signing up is free and requires no credit card. The free plan lets you explore the full dashboard and see how the AI chatbot works before paying anything. Putting a live chatbot online for your customers starts at $9.99/mo, which includes unlimited conversations.',
  },
  {
    question: 'How is ChytBot different from ChatGPT and other AI chat tools?',
    answer:
      'General AI chat tools like ChatGPT answer from broad internet knowledge and live on someone else\'s website. ChytBot is a conversational AI chatbot built for customer service: it is trained only on your business content, embeds directly on your own site, and answers questions about your products, pricing, and policies — so customers can talk to AI without you writing a single reply.',
  },
  {
    question: 'Is ChytBot a good alternative to Zendesk, Intercom, or Drift chatbots?',
    answer:
      'Yes. Enterprise tools like Zendesk, Intercom, Drift, HubSpot, and Ada bundle their chatbots into larger support suites with per-seat pricing. ChytBot focuses on one thing — an AI chatbot for customer service — at small-business pricing, with no per-seat fees and setup measured in minutes instead of weeks. If you mainly need automated customer support on your website, ChytBot covers it for a fraction of the cost.',
  },
];

// Generate FAQ structured data for SEO rich snippets
const generateFAQStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

interface FAQProps {
  showHeader?: boolean;
}

export const FAQ = ({ showHeader = true }: FAQProps) => {
  return (
    <section id="faq" className="py-20 md:py-28 bg-muted/30">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(generateFAQStructuredData())}
        </script>
      </Helmet>
      <div className="container max-w-3xl">
        {showHeader && (
          <div className="mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-3 tracking-tight aceternity-gradient-text">
              Frequently asked questions
            </h2>
            <p className="text-lg text-muted-foreground">
              Everything you need to know about ChytBot.
            </p>
          </div>
        )}

        <Accordion type="single" collapsible className="w-full AccordionRoot">
          {faqData.map((faq, index) => (
            <AccordionItem key={`item-${index}`} value={`item-${index}`}>
              <AccordionTrigger className="text-left">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent>{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
